<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  addDays,
  formatShortDate,
  formatTime,
  isDueToday,
  isOverdue,
  isSameDay,
  overdueDays,
} from './taskHelpers.js';

const props = defineProps({
  task: {
    type: Object,
    required: true,
  },
});

const { t, locale } = useI18n();

const label = computed(() => {
  const now = new Date();
  const dueDate = new Date(props.task.dueDate);
  if (props.task.status === 'done') {
    return t('TASKS.DUE.DONE_ON', {
      date: formatShortDate(dueDate, locale.value),
    });
  }
  if (isDueToday(props.task, now)) {
    if (props.task.dueTimePresent === false) {
      return t('TASKS.DUE.TODAY');
    }
    return t('TASKS.DUE.TODAY_AT', {
      time: formatTime(dueDate, locale.value),
    });
  }
  if (isOverdue(props.task, now)) {
    const days = overdueDays(props.task, now);
    return t('TASKS.DUE.OVERDUE_DAYS', { count: days });
  }
  if (isSameDay(dueDate, addDays(now, 1))) {
    if (props.task.dueTimePresent === false) {
      return t('TASKS.DUE.TOMORROW');
    }
    return t('TASKS.DUE.TOMORROW_AT', {
      time: formatTime(dueDate, locale.value),
    });
  }
  return formatShortDate(dueDate, locale.value);
});

const icon = computed(() => {
  if (isDueToday(props.task)) return 'i-lucide-clock-3 text-n-amber-10';
  if (isOverdue(props.task)) return 'i-lucide-alert-circle text-n-ruby-10';
  if (props.task.status === 'done') return 'i-lucide-check text-n-teal-10';
  return 'i-lucide-calendar text-n-slate-10';
});
</script>

<template>
  <span
    class="inline-flex items-center min-w-0 gap-1.5 text-xs text-n-slate-11"
  >
    <span class="size-3.5 shrink-0" :class="icon" />
    <span class="truncate">{{ label }}</span>
  </span>
</template>
